import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Button, Flex, Grid, Img, Text, useToast } from '@chakra-ui/react';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { User } from 'models';
import routes from 'routes';
import { checkLastVisitFromApp } from 'utils/checkLastVisitFromApp';

import AddFakeUser from 'components/home/AddFakeUser';
import FeedbackChat from 'components/home/FeedbackChat';

import { ReactComponent as LeftArrowIcon } from 'icons/back-arr.svg';
import { ReactComponent as CloseIcon } from 'icons/close.svg';
import { ReactComponent as RightArrowIcon } from 'icons/right-arr.svg';

interface Props {
  onClose?: () => void;
  user?: User;
  showHeader?: boolean;
}

const Menu: React.FC<Props> = ({ onClose, user, showHeader = false }) => {
  const [showChat, setShowChat] = useState(false);
  const adminToken = Cookies.get('sbc_admin_token');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const toast = useToast();
  const isFakeUser = localStorage.getItem('is_fake_user') === 'true';
  const [gotNewMessage, setGotNewMessage] = useState(false);
  const onCloseHandler = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!(window as any).fcSettings) return;
    (window as any).fcSettings = {
      onInit: function () {
        (window as any).fcWidget.on('unreadCount:notify', function (resp: { count: number }) {
          setGotNewMessage(resp.count > 0);
        });
      },
    };
  }, [user]);

  const menuItems = [
    {
      id: 'home',
      title: 'home',
      content: null,
      onClick: () => {
        if (from === '/leaderboard') {
          navigate('/', { state: { from: location.pathname } });
        } else {
          navigate('/');
        }
        onCloseHandler();
      },
    },
    {
      id: 'profile',
      title: 'profile',
      content: user?.avatarUrl ? (
        <Box
          w="28px"
          h="28px"
          borderRadius="10px"
          backgroundImage={user.avatarUrl}
          backgroundPosition="center center"
          backgroundSize="cover"
        />
      ) : null,
      onClick: () => {
        navigate(`${routes.profile}/`, { state: { from: location.pathname } });
        onCloseHandler();
      },
    },
    {
      id: 'leaderboard',
      title: 'leaderboard',
      content: null,
      onClick: () => {
        const currentDate = dayjs().format('YYYY-MM-DD');
        navigate(`/leaderboard/monthly/${currentDate}?friends=false&league=all`, {
          state: { from: location.pathname },
        });
        onCloseHandler();
      },
    },
    {
      id: 'friends',
      title: 'friends',
      content: null,
      onClick: () => {
        const currentDate = dayjs().format('YYYY-MM-DD');
        navigate(`/leaderboard/monthly/${currentDate}?friends=true&league=all`, { state: { from: location.pathname } });
        onCloseHandler();
      },
    },
    {
      id: 'boosters',
      title: 'boosters',
      content: null,
      onClick: () => {
        navigate(`${routes.allBoosters}/`, { state: { from: location.pathname } });
        onCloseHandler();
      },
    },
    {
      id: 'store',
      title: (
        <Box as="span">
          store{' '}
          <Box as="span" color="#ff4040">
            soon
          </Box>
        </Box>
      ),
      content: null,
      onClick: () => {
        toast({
          duration: 3000,
          isClosable: true,
          position: 'bottom',
          render: ({ onClose }) => {
            return (
              <Flex
                alignItems="center"
                justifyContent="center"
                p="5px 18px !important"
                backgroundColor="#111111"
                borderRadius="10px"
                gap="10px"
                w="min-content"
                whiteSpace="nowrap"
                margin="0 auto 10px"
                onClick={onClose}
              >
                <Box>Keep your coins for now</Box>
              </Flex>
            );
          },
        });
      },
    },
    {
      id: 'chat',
      title: (
        <Flex gap="10px">
          <Box as="span" position="relative">
            chat with devs
          </Box>
          {gotNewMessage && (
            <Flex
              as="span"
              w="20px"
              h="20px"
              borderRadius="10px"
              top="0px"
              right="-10px"
              backgroundColor="#F83C3C"
              zIndex="1"
              justifyContent="center"
              alignItems="center"
              fontSize="12px"
              textShadow={`0 -0.5px 0px rgba(0, 0, 0, 0.9)`}
            >
              1
            </Flex>
          )}
        </Flex>
      ),
      content: null,
      onClick: () => {
        setShowChat(true);
        if ((window as any).fcWidget && showChat) {
          (window as any).fcWidget.open();
        }
      },
    },
    // {
    //   id: 'app',
    //   title: 'play in the app',
    //   content: null,
    //   onClick: () => {
    //     navigate(`https://clutchpoints.onelink.me/Hz48/sbc`);
    //     onCloseHandler();
    //   },
    // },

    // {
    //   id: 'logout',
    //   title: 'logout',
    //   content: null,
    //   onClick: () => {
    //     navigate('/logout');
    //   },
    // },
  ];

  const onChatCloseHandler = () => {
    setShowChat(false);
  };

  return (
    <>
      {showChat && user && <FeedbackChat user={user} openOnMount onClose={onChatCloseHandler} />}
      <Flex
        flexDirection="column"
        gap="0px"
        w="100%"
        // h="100vh"
        flex="1"
        // pos="fixed"
        top="0"
        backgroundColor="#242424"
        zIndex="10001"
        maxW="500px"
        margin={showHeader ? '0 auto' : '66px auto 0'}
        position="relative"
      >
        {showHeader && (
          <Grid
            gridTemplateColumns="48px 1fr 48px"
            gap="8px"
            py="8px"
            px="12px"
            zIndex="1"
            w="100%"
            maxW="500px"
            mx="auto"
            mt="8px"
            mb="14px"
            transition="all 0.2s ease-in-out"
          >
            <Box display="flex" alignItems="center" justifyContent="center">
              <Box
                onClick={onCloseHandler}
                width="48px"
                minH="24px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="20px"
                borderRadius="12px"
                transition="all 0.2s ease-in-out"
                _active={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Box as={CloseIcon} />
              </Box>
            </Box>
            <Flex textAlign="center" justifyContent="center" alignItems="center">
              <Text fontSize="14px" fontWeight="800" lineHeight="1.2">
                Menu
              </Text>
            </Flex>
            <Flex
              fontSize="20px"
              lineHeight="1"
              borderRadius="12px"
              transition="all 0.2s ease-in-out"
              width="48px"
              alignItems="center"
              justifyContent="center"
              gap="3px"
            ></Flex>
          </Grid>
        )}
        <Flex flexDirection="column" w="100%">
          {menuItems
            .filter((item) =>
              location.pathname.includes('leaderboard') || location.pathname.includes('boosters')
                ? true
                : item.id !== 'home'
            )
            .map((item, index) => (
              <Flex
                display={item.id === 'logout' && localStorage.getItem('sbc_landing_for_app') ? 'none' : 'flex'}
                key={`menu-item-${index}`}
                flexDirection="row"
                alignItems="center"
                gap="8px"
                p="16px"
                borderTop="1px solid #3a3a3a"
                borderBottom="1px solid #3a3a3a"
                mb="-1px"
                w="100%"
                className="group"
                onClick={item.onClick}
                cursor="pointer"
              >
                <Box
                  fontSize="18px"
                  fontWeight="700"
                  lineHeight="1.2"
                  flex="1"
                  color={item.id !== 'logout' ? 'rgba(255, 255, 255, 1)' : 'rgba(248, 60, 60, 1)'}
                >
                  {item.title}
                </Box>
                <Flex gap="15px" alignItems="center">
                  {item.id === 'profile-pic' && item.content ? (
                    <Img
                      w="28px"
                      h="28px"
                      objectFit="cover"
                      borderRadius="10px"
                      src={item.content as any}
                      alt={item.title}
                    />
                  ) : (
                    item.content && (
                      <Box fontSize="15px" fontWeight="500" lineHeight="1.2">
                        {item.content}
                      </Box>
                    )
                  )}
                  {item.id !== 'logout' && (
                    <Box
                      opacity="0.5"
                      transition="all .2s ease"
                      _groupActive={{ opacity: 1 }}
                      as={RightArrowIcon}
                    ></Box>
                  )}
                </Flex>
              </Flex>
            ))}
          {user?.isAdmin && location.pathname === '/' && <AddFakeUser />}
          {isFakeUser && adminToken && (
            <Flex
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="8px"
              p="16px"
              borderTop="1px solid #3a3a3a"
              borderBottom="1px solid #3a3a3a"
              mb="-1px"
              w="100%"
              className="group"
              onClick={() => {
                const sbcToken = Cookies.get('sbc_access_token');
                const wasSbcToken = !!sbcToken;
                Cookies.remove('sbc_access_token');
                Cookies.remove('cp_access_token');
                if (adminToken) {
                  if (wasSbcToken) {
                    Cookies.set('sbc_access_token', adminToken);
                  }
                }
                Cookies.remove('sbc_admin_token');
                localStorage.removeItem('is_fake_user');
                window.location.reload();
              }}
              cursor="pointer"
            >
              <Box fontSize="18px" fontWeight="700" lineHeight="1.2" flex="1" color="rgba(255, 255, 255, 0.7)">
                back to admin
              </Box>
              <Flex gap="15px" alignItems="center">
                <Box opacity="0.5" transition="all .2s ease" _groupActive={{ opacity: 1 }} as={RightArrowIcon} />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default Menu;
